<template>
  <div class="event-info-cards px-4 py-6">
    <div class="info-cards-grid">
      <!-- Date -->
      <div class="info-card">
        <div class="info-card__icon-wrap info-card__icon-wrap--blue">
          <i class="far fa-calendar" aria-hidden="true"></i>
        </div>
        <div class="info-card__content">
          <span class="info-card__label">{{
            $t('Views.Lobby.EventInfoCards.date')
          }}</span>
          <span class="info-card__value">{{ formattedDate }}</span>
        </div>
      </div>

      <!-- Time -->
      <div class="info-card">
        <div class="info-card__icon-wrap info-card__icon-wrap--purple">
          <i class="far fa-clock" aria-hidden="true"></i>
        </div>
        <div class="info-card__content">
          <span class="info-card__label">{{
            $t('Views.Lobby.EventInfoCards.time')
          }}</span>
          <span class="info-card__value">{{ formattedTime }}</span>
        </div>
      </div>

      <!-- Location -->
      <div class="info-card">
        <div class="info-card__icon-wrap info-card__icon-wrap--orange">
          <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
        </div>
        <div class="info-card__content">
          <span class="info-card__label">{{
            $t('Views.Lobby.EventInfoCards.location')
          }}</span>
          <span class="info-card__value">{{
            location || $t('Views.Lobby.EventInfoCards.tba')
          }}</span>
        </div>
      </div>

      <!-- Attendees -->
      <div class="info-card">
        <div class="info-card__icon-wrap info-card__icon-wrap--teal">
          <i class="far fa-user" aria-hidden="true"></i>
        </div>
        <div class="info-card__content">
          <span class="info-card__label">{{
            $t('Views.Lobby.EventInfoCards.attendees')
          }}</span>
          <span class="info-card__value">{{ formattedAttendees }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Vue from 'vue';
import { intlLocale } from '@/i18n';

export default {
  name: 'EventInfoCards',
  props: {
    data: {
      type: Object,
      default: null
    }
  },
  created() {
    if (!this.totalCount) {
      this.fetchUserTotalCount();
    }
  },
  methods: {
    ...mapActions('Users', ['fetchUserTotalCount'])
  },
  computed: {
    ...mapGetters('Users', { totalCount: 'getUserTotalCount' }),
    ...mapGetters('CurrentEvent', {
      getEvent: 'getEvent',
      lobbyElements: 'lobbyElements'
    }),
    // Use the passed data prop first, fall back to the Vuex store
    eventData() {
      const ev = this.data || this.getEvent || {};
      console.log('[EventInfoCards] event object:', ev);
      return ev;
    },
    formattedDate() {
      const start = this.eventData.startDate;
      const end = this.eventData.endDate;
      if (!start) return '—';
      const s = Vue.moment(start);
      const e = end ? Vue.moment(end) : null;
      if (!e || s.isSame(e, 'day')) return s.format('MMM D, YYYY');
      if (s.isSame(e, 'month'))
        return `${s.format('MMM D')} – ${e.format('D, YYYY')}`;
      if (s.isSame(e, 'year'))
        return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
      return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
    },
    formattedTime() {
      const start = this.eventData.startDate;
      if (!start) return '—';
      const s = Vue.moment(start);
      const e = this.eventData.endDate
        ? Vue.moment(this.eventData.endDate)
        : null;
      // If midnight UTC/local treat as all-day
      if (s.hours() === 0 && s.minutes() === 0)
        return this.$t('Views.Lobby.EventInfoCards.allDay');
      if (!e) return s.format('HH:mm');
      return `${s.format('HH:mm')} – ${e.format('HH:mm')}`;
    },
    location() {
      const ev = this.eventData;
      // Check every plausible field name in the event object
      const fromEvent =
        ev.location ||
        ev.locationText ||
        ev.venue ||
        ev.venueName ||
        ev.locationName ||
        ev.physicalLocation ||
        ev.address ||
        ev.place ||
        ev.city ||
        '';
      if (fromEvent) return fromEvent;
      // Fall back to banner lobby-element options (admin may store it there)
      const bannerOpts =
        this.lobbyElements?.find((x) => x.id === 'banner')?.options ?? {};
      return (
        bannerOpts.location || bannerOpts.locationText || bannerOpts.venue || ''
      );
    },
    formattedAttendees() {
      const count = this.totalCount;
      if (!count) return '—';
      return count.toLocaleString(intlLocale());
    }
  }
};
</script>

<style scoped lang="scss">
.event-info-cards {
  font-family: 'Poppins', sans-serif;
}

.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

.info-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  min-width: 0;

  @media (min-width: 768px) {
    gap: 16px;
    padding: 20px;
  }

  @media (max-width: 640px) {
    padding: 12px 10px;
    gap: 10px;
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    flex-shrink: 0;
    font-size: 18px;

    &--blue {
      background: #e6f7f5;
      color: var(--color-primary, #047873);
    }
    &--purple {
      background: #f3f0ff;
      color: #7c3aed;
    }
    &--orange {
      background: #fff4ed;
      color: #ea580c;
    }
    &--teal {
      background: #ecfdf5;
      color: #16a34a;
    }

    @media (max-width: 640px) {
      width: 36px;
      height: 36px;
      font-size: 15px;
      border-radius: 10px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  &__label {
    font-size: 14px;
    color: #6b7280;
    font-weight: 400;
    line-height: 1.2;

    @media (max-width: 640px) {
      font-size: 12px;
    }
  }

  &__value {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 640px) {
      font-size: 13px;
    }
  }
}
</style>
